import React, { useState, useEffect, useContext, useRef } from 'react';
import {
	Container,
	Stack,
	AppBar,
	Toolbar,
	Typography,
	Dialog,
	IconButton,
	TableContainer,
	Table,
	TableCell,
	TableRow,
	TableHead,
	TableBody,
	Divider
} from '@mui/material';
import { isEmpty } from 'lodash';

import { useParams } from 'react-router-dom';
import { DataContext } from '../../../context/DataContext';

import { getRol, getToken, getUser } from '../../../api/Account';
import ReactToPrint from 'react-to-print';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import NoData from '../../../components/NoData';

import { PrintReport } from '../../../components/PrintReport';
import moment from 'moment';
import { getSucursalByUserAsync } from '../../../api/Users';
import { simpleMessage } from '../../../helpers/Helpers';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowDown } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx/xlsx.mjs';
import { getGastosHistoryAsync } from '../../../api/AdmonReport';
import { PrintReportGerencial } from '../../../components/PrintReportGerencial';

const ReporteGastosHistory = () => {
	const [data, setData] = useState([]);

	const [sucursalName, setSucursalName] = useState('');
	const [sucursalList, setSucursalList] = useState([]);
	const compRef = useRef();
	const { selectedSucursal, fechaDesde, fechaHasta, selectedGestor } = useParams();

	const { setIsLoading, setIsUnautorized, title, slogan, icon } = useContext(DataContext);

	const token = getToken();
	const user = getUser();
	const rol = getRol();

	const formatDate = (d) => moment(d).format('L');

	const formatMoney = (v) =>
		new Intl.NumberFormat('es-NI', {
			style: 'currency',
			currency: 'NIO'
		}).format(v);

	const handleError = (result) => {
		setIsLoading(false);
		if (result.error === 'Unauthorized') return setIsUnautorized(true);
		simpleMessage(result.error, 'error');
	};

	const getSucursals = async () => {
		const r = await getSucursalByUserAsync(token);
		if (!r.statusResponse) return handleError(r);

		const results = r.data.result;
		const filtered =
			selectedSucursal === 't'
				? results
				: results.filter((x) => x.id.toString() === selectedSucursal);

		setSucursalList(filtered);

		return filtered;
	};

	const fetchData = async () => {
		const data = {
			sucursalId: selectedSucursal === 't' ? 0 : selectedSucursal,
			desde: fechaDesde.toString(),
			hasta: fechaHasta.toString(),
			gestorId: selectedGestor === 't' ? 0 : selectedGestor
		};

		const result = await getGastosHistoryAsync(token, data);
		if (!result.statusResponse) return handleError(result);

		setData(result.data.result);
		setIsLoading(false);
	};

	useEffect(() => {
		(async () => {
			setIsLoading(true);
			await getSucursals();
			await fetchData();
		})();
	}, []);

	const pageStyle = `
  @media print {
    html, body {
      height: initial !important;
      overflow: initial !important;
      -webkit-print-color-adjust: exact;
    }
  }
  @media all {
    .pagebreak {
      display: none;
    }
  }
  @media print {
    .pagebreak {
      page-break-before: always;
    }
  }  
  @page {  margin: 3mm; }
`;

	const exportExcel = () => {
		let exp = [];
		data.map((item) => {
			let obj = {
				'#': item.id,
				fecha: formatDate(item.fecha),
				'Cuenta Contable': item.countNumber,
				Concepto: item.description,
				Monto: item.monto,
				'Realizado Por': item.realizadoPor,
				Sucursal: item.sucursalNombre
			};

			exp.push(obj);
		});
		var bk = XLSX.utils.book_new(),
			bs = XLSX.utils.json_to_sheet(exp);

		XLSX.utils.book_append_sheet(bk, bs, 'historialGastos');
		XLSX.writeFile(bk, 'HistorialGastos.xlsx');
	};

	let montoTotalConsol = data.reduce((acc, r) => acc + Number(r.monto || 0), 0);

	const DataTable = ({ data, headerFontSize, dataFontSize, isForPrint = false }) => {
		const totalGastos = data.reduce((acc, r) => acc + Number(r.monto || 0), 0);

		montoTotalConsol += totalGastos;
		const columns = [
			{ key: 'id', label: '#', align: 'center', getValue: (r) => r.id },
			{
				key: 'fecha',
				label: 'Fecha',
				align: 'center',
				getValue: (r) => formatDate(r.fecha)
			},
			{
				key: 'countNumber',
				label: 'Cuenta Contable',
				align: 'center',
				getValue: (r) => r.countNumber
			},
			{
				key: 'description',
				label: 'Concepto',
				align: 'left',
				getValue: (r) => r.description
			},
			{
				key: 'monto',
				label: 'Monto',
				align: 'center',
				getValue: (r) => formatMoney(r.monto)
			},
			{
				key: 'realizadoPor',
				label: 'Realizado Por',
				align: 'left',
				getValue: (r) => r.realizadoPor
			}
		];

		return (
			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow>
							{columns.map((col) => (
								<TableCell
									key={col.key}
									align={col.align}
									style={{ fontSize: headerFontSize }}
									className="text-primary fw-bold"
								>
									{col.label}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{data.map((r) => (
							<TableRow key={r.id}>
								{columns.map((col) => (
									<TableCell
										key={col.key}
										align={col.align}
										style={{ fontSize: dataFontSize }}
										className={isForPrint ? 'text-dark' : ''}
									>
										{col.getValue(r)}
									</TableCell>
								))}
							</TableRow>
						))}

						{/* 👉 NUEVA FILA DE TOTAL */}
						<TableRow>
							<TableCell
								colSpan={columns.length - 2}
								align="right"
								style={{ fontSize: headerFontSize }}
								className={
									isForPrint ? 'text-dark fw-bold' : 'text-primary fw-bold'
								}
							>
								Total de Gastos:
							</TableCell>

							<TableCell
								colSpan={2}
								align="left"
								style={{ fontSize: headerFontSize }}
								className="text-primary fw-bold"
							>
								{formatMoney(totalGastos)}
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</TableContainer>
		);
	};

	const paramText = (
		<Stack direction={'row'} justifyContent="space-between">
			<Stack>
				<Typography
					sx={{
						color: '#2196f3',
						textAlign: 'left'
					}}
					fontSize={11}
				>
					Parametros:
				</Typography>
				<Typography
					fontSize={11}
					sx={{
						textAlign: 'left'
					}}
				>
					{`Sucursal: ${
						selectedSucursal === 't' ? 'Todas' : sucursalList[0]?.description || ''
					}`}
				</Typography>

				<Typography fontSize={11} textAlign="left">
					{`Desde: ${moment(fechaDesde).format('L')}`}
				</Typography>
				<Typography fontSize={11} textAlign="left">
					{`Hasta:  ${moment(fechaHasta).format('L')}`}
				</Typography>
			</Stack>

			<Stack>
				<Typography
					sx={{
						color: '#2196f3',
						textAlign: 'left'
					}}
					fontSize={11}
				>
					Usuario:
				</Typography>
				<Typography fontSize={11} textAlign="left">
					{user}
				</Typography>
				<Typography fontSize={11} textAlign="left">
					{rol}
				</Typography>
				<Typography fontSize={11} textAlign="left">
					{`Fecha y Hora: ${moment().format('DD/MM/YYYY hh:mm A')}`}
				</Typography>
			</Stack>
		</Stack>
	);

	return (
		<div>
			<Dialog fullScreen open={true}>
				<AppBar sx={{ position: 'relative' }}>
					<Toolbar>
						<img loading="eager" src={icon} alt="logo" style={{ height: 40 }} />
						<Typography
							sx={{ ml: 2, flex: 1, textAlign: 'center' }}
							variant="h5"
							component="div"
						>
							{`${title} `}
						</Typography>
						<img loading="eager" src={icon} alt="logo" style={{ height: 40 }} />
					</Toolbar>
				</AppBar>

				<Stack display="flex" justifyContent="center">
					<Stack
						direction={'row'}
						justifyContent="space-between"
						paddingLeft={5}
						paddingRight={5}
						marginTop={1}
						marginBottom={1}
					>
						<IconButton variant="outlined" onClick={() => exportExcel()}>
							<FontAwesomeIcon
								icon={faCloudArrowDown}
								style={{ fontSize: 40, color: '#00a152', width: 40 }}
							/>
						</IconButton>

						<Stack>
							<Typography
								sx={{
									color: '#2196f3',
									textAlign: 'center',
									fontWeight: 'bold'
								}}
							>
								{slogan}
							</Typography>
							<Typography
								sx={{
									color: '#2196f3',
									textAlign: 'center',
									fontWeight: 'bold'
								}}
								variant="h6"
								component="div"
							>
								Reporte Gastos
							</Typography>
						</Stack>

						<ReactToPrint
							trigger={() => {
								return (
									<IconButton variant="outlined">
										<PrintRoundedIcon
											style={{ fontSize: 40, color: '#2979ff', width: 40 }}
										/>
									</IconButton>
								);
							}}
							content={() => compRef.current}
							pageStyle={pageStyle}
						/>
					</Stack>

					<Container maxWidth={false} sx={{ textAlign: 'center' }}>
						{paramText}
					</Container>
				</Stack>

				<Container maxWidth={false} sx={{ textAlign: 'center' }}>
					<hr />

					{isEmpty(data) ? (
						<NoData />
					) : (
						sucursalList.map((suc) => {
							const sSucursal = data.filter(
								(c) => c.sucursalNombre === suc.description
							);

							return (
								<Stack key={suc.description}>
									<Typography
										fontSize={11}
										color="#2196f3"
										textAlign="left"
										fontWeight="bold"
									>
										Sucursal: {suc.description}
									</Typography>

									{sSucursal.length === 0 ? (
										<Typography
											fontSize={11}
											color="#2196f3"
											textAlign="center"
											fontWeight="bold"
										>
											No hay datos para mostrar
										</Typography>
									) : (
										<DataTable data={sSucursal} />
									)}
								</Stack>
							);
						})
					)}

					{selectedSucursal === 't' ? (
						<Stack>
							<Typography
								sx={{
									color: '#2196f3',
									textAlign: 'center'
								}}
							>
								Consolidado
							</Typography>
							<hr />
							<Stack direction="row" flex="row" justifyContent="space-around">
								<Stack textAlign="center">
									<Typography
										style={{
											fontWeight: 'bold',
											color: '#03a9f4',
											fontSize: 11
										}}
									>
										Consolidado de Transacciones
									</Typography>
									<Typography style={{ fontSize: 11 }}>{data.length}</Typography>
								</Stack>

								<Stack textAlign="center">
									<Typography
										style={{
											fontWeight: 'bold',
											color: '#03a9f4',
											fontSize: 11
										}}
									>
										Consolidado Monto Total
									</Typography>
									<Typography style={{ fontSize: 11 }}>
										{new Intl.NumberFormat('es-NI', {
											style: 'currency',
											currency: 'NIO'
										}).format(montoTotalConsol)}
									</Typography>
								</Stack>
							</Stack>
							<hr />
						</Stack>
					) : (
						<></>
					)}
				</Container>
			</Dialog>

			<div
				style={{
					display: 'none'
				}}
			>
				<PrintReportGerencial
					ref={compRef}
					titulo={'Reporte de Gastos'}
					fecha={`Fecha: ${moment().format('L')}`}
					rango={`Del ${moment(fechaDesde).format('L')} al ${moment(fechaHasta).format(
						'L'
					)}`}
					isLandscape={false}
				>
					<Container maxWidth={false} sx={{ textAlign: 'center' }}>
						{paramText}
					</Container>

					<Container maxWidth={false} sx={{ textAlign: 'center' }}>
						<hr />

						{isEmpty(data) ? (
							<NoData />
						) : (
							sucursalList.map((suc) => {
								const sSucursal = data.filter(
									(c) => c.sucursalNombre === suc.description
								);

								return (
									<Stack key={suc.description}>
										<Typography
											fontSize={11}
											color="#2196f3"
											textAlign="left"
											fontWeight="bold"
										>
											Sucursal: {suc.description}
										</Typography>

										{sSucursal.length === 0 ? (
											<Typography
												fontSize={11}
												color="#2196f3"
												textAlign="center"
												fontWeight="bold"
											>
												No hay datos para mostrar
											</Typography>
										) : (
											<DataTable
												data={sSucursal}
												headerFontSize={11}
												dataFontSize={10}
												isForPrint={true}
											/>
										)}
									</Stack>
								);
							})
						)}

						{selectedSucursal === 't' ? (
							<Stack>
								<Typography
									sx={{
										color: '#2196f3',
										textAlign: 'center'
									}}
								>
									Consolidado
								</Typography>
								<hr />
								<Stack direction="row" flex="row" justifyContent="space-around">
									<Stack textAlign="center">
										<Typography
											style={{
												fontWeight: 'bold',
												color: '#03a9f4',
												fontSize: 11
											}}
										>
											Consolidado de Transacciones
										</Typography>
										<Typography style={{ fontSize: 11 }}>
											{data.length}
										</Typography>
									</Stack>

									<Stack textAlign="center">
										<Typography
											style={{
												fontWeight: 'bold',
												color: '#03a9f4',
												fontSize: 11
											}}
										>
											Consolidado Monto Total
										</Typography>
										<Typography style={{ fontSize: 11 }}>
											{new Intl.NumberFormat('es-NI', {
												style: 'currency',
												currency: 'NIO'
											}).format(montoTotalConsol)}
										</Typography>
									</Stack>
								</Stack>
								<hr />
							</Stack>
						) : null}
					</Container>
				</PrintReportGerencial>
			</div>
		</div>
	);
};

export default ReporteGastosHistory;
